import { Suspense, lazy, useCallback, useMemo } from 'react';
import { PageLoading } from 'components/atoms/loading';
import { LocaleIntlProvider } from 'components/i18n/LocaleIntlProvider';
import type { ActionAgentSectionProps } from 'components/organisms/agent-trigger';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { useAiFeatureFlags } from 'hooks/use-feature-flags';
import type {
  AgentTriggerRenderProps,
  SheetInstanceRenderProps,
  HostAPI,
  HostComponentType,
  RenderComponentRequest,
  LoadingRenderProps,
} from 'types/host-components';

// Lazy: SheetInChatView is only invoked when the remote host renders a
// 'sheet_instance' request. Static import was pinning the whole sheets
// graph into the shell.
const SheetInChatView = lazy(() =>
  import('components/organisms/sheets/sheet-in-chat/sheet-in-chat-view').then(
    (m) => ({ default: m.SheetInChatView }),
  ),
);

// Lazy: ActionAgentSection drags the automations filter UI which pulls
// the tasks-organism criteria tree (~84 kB). Only rendered when the host
// requests an 'agent_trigger' component.
const ActionAgentSection = lazy(() =>
  import(
    'components/organisms/agent-trigger/components/action-agent-section'
  ).then((m) => ({ default: m.ActionAgentSection })),
);

/**
 * Builds the hostAPI (renderComponent) used by the agent builder remote and
 * document agent preview. Share this so preview route has the same HostAPI
 * as the main agent builder (fixes "useHostAPI must be used within a HostAPIProvider").
 */
export function useAgentBuilderHostAPI(): HostAPI {
  const { hasAccessToResource } = useWithAccess();
  const { isSignalsEnabled } = useAiFeatureFlags();

  const renderComponent = useCallback(
    <T extends HostComponentType>(request: RenderComponentRequest<T>) => {
      switch (request.type) {
        case 'sheet_instance': {
          const props = request.props as SheetInstanceRenderProps;

          return (
            <LocaleIntlProvider fallbackOnEmptyString>
              <Suspense fallback={null}>
                <SheetInChatView
                  projectId={props.projectId}
                  sheetId={props.sheetId}
                  tabId={props.sheetTabId}
                  readonly={props.readonly}
                  showInfoBar={props.showInfoBar}
                  sourceType={props.sourceType}
                  sourceId={props.sourceId}
                  ruleIds={props.ruleIds}
                  conversationId={props.conversationId}
                  validationType={props.validationType}
                />
              </Suspense>
            </LocaleIntlProvider>
          );
        }
        case 'agent_trigger': {
          const props = request.props as AgentTriggerRenderProps &
            ActionAgentSectionProps;

          return (
            <LocaleIntlProvider fallbackOnEmptyString>
              <Suspense fallback={null}>
                <ActionAgentSection
                  shouldShowActionCheckbox={props.shouldShowActionCheckbox}
                  actionEnabled={props.actionEnabled}
                  onActionEnabledChange={props.onActionEnabledChange}
                  agentTriggerRegistryConfig={props.agentTriggerRegistryConfig}
                  onAgentTriggerRegistryConfigChange={
                    props.onAgentTriggerRegistryConfigChange
                  }
                  defaultDocumentAgentId={props.defaultDocumentAgentId}
                  onAddConditions={props.onAddConditions}
                  isSignalsEnabled={isSignalsEnabled}
                />
              </Suspense>
            </LocaleIntlProvider>
          );
        }
        case 'loading': {
          const props = request.props as LoadingRenderProps;

          return (
            <LocaleIntlProvider fallbackOnEmptyString>
              <PageLoading size={props.size} />
            </LocaleIntlProvider>
          );
        }
        default:
          return null;
      }
    },
    [isSignalsEnabled],
  );

  return useMemo(
    () => ({
      renderComponent,
      hasAccessToResource,
    }),
    [renderComponent, hasAccessToResource],
  );
}
